import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { Step } from "nextstepjs";
import { DefaultButton } from "./DefaultButton";
import { TypewriterText } from "./TypewriterText";
import { useNextStep } from "nextstepjs";
import { requestHomeModesFocus } from "@/lib/homeModesFocus";
import { t } from "@/lib/i18n";

const COMPACT_BREAKPOINT = 640;
const EDGE_GAP = 96;
const STACKED_HORIZONTAL_PADDING = 32;

const HERO_SRC = "https://cdn.navalabs.io/icons/valentino-head_fh2q2k.png";

interface ExtendedStep extends Step {
  hideControls?: boolean;
  image?: string;
  selector?: string;
}

interface TourCardProps {
  step: ExtendedStep;
  currentStep: number;
  totalSteps: number;
  nextStep: () => void;
  prevStep: () => void;
  skipTour?: () => void;
  arrow: React.ReactNode;
}

const TourCard = ({
  step,
  currentStep,
  totalSteps,
  nextStep,
  // prevStep,
  skipTour,
  // arrow,
}: TourCardProps) => {
  const [isSkipping, setIsSkipping] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [avatarPlacement, setAvatarPlacement] = useState<
    "side" | "top" | "bottom"
  >("side");

  // Guard against undefined step during tour transitions (after hooks)
  const hidden = step?.hideControls;
  // bubbleWidth and xOffset are now handled via direct DOM manipulation to reduce render cycles
  // const [bubbleWidth, setBubbleWidth] = useState<number | null>(null);
  // const [xOffset, setXOffset] = useState(0);

  const bubbleRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { currentTour } = useNextStep();

  // Check if this is the navigation step (last step)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isNavigationStep = step?.selector?.includes("navigation");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isClickThroughStep = currentStep === totalSteps - 1; // Last step is click-through

  const handleSkip = async () => {
    setIsSkipping(true);
    try {
      skipTour?.();
    } catch (error) {
      console.error("Skip failed:", error);
    } finally {
      setIsSkipping(false);
    }
  };

  const handleNext = useCallback(async () => {
    if (isSkipping || isAdvancing) {
      return;
    }

    // For homeModesTour, focus the next hotspot before advancing to the next step
    if (
      currentTour === "homeModesTour" &&
      typeof currentStep === "number" &&
      currentStep + 1 < totalSteps
    ) {
      setIsAdvancing(true);
      try {
        await requestHomeModesFocus(currentStep + 1);
      } catch (error) {
        console.warn("[Tour] homeModes focus before next step failed", error);
      } finally {
        setIsAdvancing(false);
      }
    }

    nextStep();
  }, [currentTour, currentStep, isAdvancing, isSkipping, nextStep, totalSteps]);

  const updateAvatarPlacement = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const bubble = bubbleRef.current;
    const arrow = arrowRef.current;
    const container = containerRef.current;

    if (!bubble || !arrow || !container) {
      return;
    }

    const viewportWidth = window.innerWidth;

    // Desktop view: use "side" placement with natural width (only for questPanelTour)
    if (
      viewportWidth > COMPACT_BREAKPOINT &&
      currentTour === "questPanelTour"
    ) {
      if (avatarPlacement !== "side") {
        setAvatarPlacement("side");
        return;
      }

      // Find nextstepjs wrapper and set position for desktop
      // NOTE: -30% is a hardcoded value that works for the current quest panel position (right side of screen).
      // If the quest panel moves or screen sizes change significantly, this value may need adjustment.
      const nextstepWrapper = document.querySelector(
        '[data-name="nextstep-card"]',
      ) as HTMLElement | null;

      if (nextstepWrapper) {
        nextstepWrapper.style.left = "-30%";
      }

      // Reset styles for desktop
      bubble.style.width = "";
      bubble.style.maxWidth = "";
      bubble.style.transform = "";
      arrow.style.left = "";
      container.style.opacity = "1";
      return;
    }

    // Reset nextstepjs wrapper position for non-questPanelTour (cleanup)
    const nextstepWrapperCleanup = document.querySelector(
      '[data-name="nextstep-card"]',
    ) as HTMLElement | null;
    if (nextstepWrapperCleanup) {
      nextstepWrapperCleanup.style.left = "50%";
    }

    const rect = bubble.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    let nextPlacement: "top" | "bottom" = "top";

    if (rect.top < EDGE_GAP) {
      nextPlacement = "bottom";
    }

    if (rect.bottom > viewportHeight - EDGE_GAP) {
      nextPlacement = "top";
    }

    // If placement changes, re-render to apply new classes — but still
    // continue and apply width/clamp using the current layout so the bubble
    // isn't left in an unconstrained state between renders.
    if (avatarPlacement !== nextPlacement) {
      setAvatarPlacement(nextPlacement);
    }

    // Calculate widths and offsets
    const computedWidth = Math.max(
      viewportWidth - STACKED_HORIZONTAL_PADDING,
      0,
    );

    // Reset transform before measuring so we read the bubble's natural position
    // (post any ancestor transforms like the modes dialog's zoom animation).
    bubble.style.transform = "";
    arrow.style.left = "";

    // Apply width directly
    bubble.style.width = `${computedWidth}px`;
    bubble.style.maxWidth = `calc(${viewportWidth > COMPACT_BREAKPOINT ? "40vw" : "100vw"} - ${STACKED_HORIZONTAL_PADDING}px)`;

    // Clamp the bubble to fit within the viewport — anchoring to a target
    // inside a narrow/animating dialog can leave the bubble off-center and
    // clipped at the screen edge. We compute the smallest shift that brings
    // both edges inside [halfPadding, viewportWidth - halfPadding]. The arrow
    // moves in the opposite direction so it still points at the hero (which
    // stays anchored above the target).
    const bubbleRect = bubble.getBoundingClientRect();
    const halfPadding = STACKED_HORIZONTAL_PADDING / 2;
    const overflowRight = bubbleRect.right - (viewportWidth - halfPadding);
    const overflowLeft = halfPadding - bubbleRect.left;
    let shift = 0;
    if (overflowRight > 0) shift = -overflowRight;
    if (overflowLeft > 0) shift = overflowLeft;

    bubble.style.transform = shift ? `translateX(${shift}px)` : "";
    arrow.style.left = shift ? `calc(50% - ${shift}px)` : "";

    // Reveal the container
    container.style.opacity = "1";
  }, [avatarPlacement, currentTour]);

  useLayoutEffect(() => {
    updateAvatarPlacement();

    if (typeof window === "undefined") {
      return;
    }

    const handleUpdate = () => updateAvatarPlacement();

    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true);

    const bubble = bubbleRef.current;
    let observer: ResizeObserver | undefined;

    if (bubble && "ResizeObserver" in window) {
      observer = new ResizeObserver(() => updateAvatarPlacement());
      observer.observe(bubble);
    }

    // Poll to catch parent settlement/animations. Must outlast the longest
    // refreshTourLayout() delay (currently 1000ms in home.tsx) so that the
    // clamp re-runs after nextstepjs re-measures the target's position.
    let frameId: number;
    const startTime = performance.now();
    const poll = () => {
      updateAvatarPlacement();
      if (performance.now() - startTime < 1500) {
        frameId = requestAnimationFrame(poll);
      }
    };
    frameId = requestAnimationFrame(poll);

    // Watch the nextstep-card wrapper for position/size changes that occur
    // after polling stops (e.g. dialog/animation settle long after mount).
    const nextstepCard = document.querySelector(
      '[data-name="nextstep-card"]',
    ) as HTMLElement | null;
    let cardObserver: ResizeObserver | undefined;
    if (nextstepCard && "ResizeObserver" in window) {
      cardObserver = new ResizeObserver(() => updateAvatarPlacement());
      cardObserver.observe(nextstepCard);
    }

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
      observer?.disconnect();
      cardObserver?.disconnect();
      cancelAnimationFrame(frameId);
    };
  }, [updateAvatarPlacement, step, currentStep]);

  const containerClasses = cn(
    "flex gap-4 z-50",
    avatarPlacement === "side"
      ? "flex-row items-center w-[150%]"
      : "flex-col items-center mx-auto",
    avatarPlacement === "bottom" && "flex-col-reverse",
  );

  const bubbleWrapperClasses = cn(
    "relative",
    //avatarPlacement === "side" ? "flex-shrink-0" : "w-full",
  );

  const bubbleClasses = cn(
    "speech-bubble-container text-black p-6 relative",
    avatarPlacement === "side" ? "max-w-md" : "w-full",
  );

  const arrowClasses = cn(
    "absolute w-6 h-auto pointer-events-none",
    avatarPlacement === "side" &&
      "-left-5.75 top-1/2 -translate-y-1/2 rotate-90",
    avatarPlacement === "top" && "left-1/2 -translate-x-1/2 -top-4 rotate-180",
    avatarPlacement === "bottom" && "left-1/2 -translate-x-1/2 -bottom-3",
  );

  const stackedWidthStyle =
    avatarPlacement === "side"
      ? undefined
      : {
          // Widths are now handled via direct DOM manipulation in updateAvatarPlacement
          width: undefined,
          maxWidth: undefined,
        };

  const isExtraTrophyTour =
    currentTour === "rushExtraTrophyTour" ||
    currentTour === "grandRushExtraTrophyTour";

  // Guard against undefined step during tour transitions
  if (!step) return null;

  return (
    <div
      ref={containerRef}
      className={containerClasses}
      style={{
        imageRendering: "pixelated",
        opacity: 0, // Start hidden, revealed by updateAvatarPlacement
        transition: "opacity 0.1s ease-in",
        ...stackedWidthStyle,
      }}
    >
      {/* HERO_SRC Character positioned outside the bubble */}
      <div className="shrink-0">
        <img
          src={HERO_SRC}
          alt={t("tourCard.altTexts.hero")}
          className="w-20"
        />
      </div>

      {/* Speech Bubble */}
      <div
        ref={bubbleRef}
        className={bubbleWrapperClasses}
        style={{
          ...stackedWidthStyle,
          // transform handled via direct DOM
        }}
      >
        <div className={bubbleClasses}>
          {/* Speech bubble arrow pointing to the hero character */}
          <svg
            ref={arrowRef}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 7 4"
            fill="none"
            className={arrowClasses}
            // left style handled via direct DOM
          >
            <path
              d="M4.08301 3.5V2.5H5.24967V1.5H6.41634V0.5H0.583008V1.5H1.74967V2.5H2.91634V3.5H4.08301Z"
              fill="white"
            />
            <path
              d="M4.08333 3.5V2.5H5.25V1.5H6.41667V0.5H0.583333V1.5H1.75V2.5H2.91667V3.5H4.08333ZM7 2H5.83333V3H4.66667V4H2.33333V3H1.16667V2H0V0H7V2Z"
              fill="white"
            />
          </svg>

          {/*<div className="mb-2">
            <h3 className="text-lg font-bold">{step.title}</h3>
          </div>*/}

          <div className="text-sm leading-tight tracking-wide text-gray-800 mb-3 relative">
            {/* Invisible full content to reserve space */}
            <span className="invisible" aria-hidden="true">
              {step.content}
            </span>
            {/* Typing text overlaid on top */}
            <TypewriterText
              content={step.content as string}
              as="span"
              charDelay={28}
              startDelay={120}
              className="absolute inset-0"
            />
          </div>

          {!hidden && (
            <div className="flex justify-between items-center">
              {totalSteps > 1 && (
                <div className="text-xs text-gray-400">
                  {t("tourCard.step")} {currentStep + 1} {t("tourCard.of")}{" "}
                  {totalSteps}
                </div>
              )}
              <div className="flex gap-2">
                {step.showSkip && (
                  <button
                    onClick={handleSkip}
                    className={`${isExtraTrophyTour && "uppercase text-black/50! hover:text-black/70!"} text-xs font-bold px-3 py-1 text-black rounded disabled:opacity-50 hover:cursor-pointer`}
                    disabled={isSkipping}
                  >
                    {isSkipping
                      ? "..."
                      : isExtraTrophyTour
                        ? t("tourCard.doNotShowAgain")
                        : t("tourCard.skip")}
                  </button>
                )}
                <DefaultButton
                  onClick={handleNext}
                  className="text-sm font-bold py-0! h-8 text-white rounded hover:cursor-pointer"
                  disabled={isSkipping || isAdvancing}
                >
                  {totalSteps === 1
                    ? t("tourCard.letsGo")
                    : t("tourCard.next")}
                </DefaultButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TourCard;
